import {
  ArrowBack,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  Delete,
  Edit,
  PlaylistAddCheck,
  QrCode2,
} from '@mui/icons-material'
import { Alert, Box, Button, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material'
import axios from 'axios'
import { isAdmin } from 'chezy-inventory-backend/bin/auth/AuthenticationContext'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { InventoryItem } from 'chezy-inventory-backend/bin/database/types/InventoryItem'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { omit } from 'lodash'
import { useSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import useSound from 'use-sound'

import err_3 from '../../e-collision.mp3'
import err_2 from '../../e-mesh.mp3'
import err_1 from '../../e-rebuild.mp3'
import success_sound2 from '../../success-ack.mp3'
import success_sound from '../../success.mp3'
import { AuthContext } from '../../utils/authContext'
import { BinWithChildren } from '../../utils/createBinTree'
import { getBaseURL } from '../../utils/getBaseURL'

import { AssessItemDialog, ItemConditionData } from './assessItemDialog'
import { BinProgressBar } from './binProgress'
import { useInventoryContext } from './inventoryBrowserContext'
import { relevantSubtreeRecursiveSearch } from './utils'

interface Notice {
  kind: 'warning' | 'success' | 'error'
  message?: string
  forceAck?: boolean
  persistent?: boolean
}

const NoticeComponent = (props: { notice: Notice; onClear: () => void }) => {
  const [blink, setBlink] = useState(false)
  const interval = useRef<NodeJS.Timer | undefined>()

  useEffect(() => {
    if (props.notice.forceAck) {
      clearInterval(interval.current)
      interval.current = setInterval(() => {
        setBlink((b) => !b)
      }, 500)
    } else {
      clearInterval(interval.current)
      interval.current = undefined
      setBlink(false)
    }
  }, [props.notice])

  return (
    <Alert
      severity={props.notice.kind}
      variant="filled"
      style={{
        fontSize: 24,
        display: 'flex',
        alignItems: 'center',
      }}
      action={
        props.notice.forceAck ? (
          <Button onClick={props.onClear} color="inherit" size="large">
            Acknowledge
          </Button>
        ) : undefined
      }
    >
      {props.notice.forceAck && <div style={blink ? { fontWeight: 'bold' } : undefined}>Attention!</div>}
      {props.notice.message}
    </Alert>
  )
}

const flattenTreeToBins = (tree: BinWithChildren): Bin[] => {
  const bin = { ...omit(tree, 'children') }
  return [bin, ...(tree.children?.flatMap((c) => flattenTreeToBins(c)) ?? [])]
}

const parentTote = (binId: string, allBins: Bin[]): Bin | undefined => {
  const bin = allBins.find((bin) => bin.id === binId)
  if (!bin) return undefined
  if (!bin?.parent_id) return bin
  else return parentTote(bin.parent_id, allBins)
}

export const InventoryBinView = () => {
  const authContext = useContext(AuthContext)
  const { enqueueSnackbar } = useSnackbar()
  const { binId } = useParams()
  const { binTree, activeInventoryItems, allBins, refresh, activeInventoryMetadata } = useInventoryContext()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const searchFieldRef = useRef<HTMLInputElement>()
  const [itemUndergoingEdits, setItemUndergoingEdits] = useState<(InventoryItem & Item) | undefined>(undefined)
  const [isPresentingMandatoryEditWindow, setIsPresentingMandatoryEditWindow] = useState<boolean>(false)
  const [notice, setNotice] = useState<Notice | undefined>()

  const bin = useMemo(() => binTree.find((b) => b.id === binId), [binTree, binId])

  const items = useMemo(() => {
    const bins = relevantSubtreeRecursiveSearch(
      bin ? [bin] : [],
      activeInventoryItems.map((item) => item.bin_id)
    )
      .flatMap(flattenTreeToBins)
      .map((bin) => bin.id)
    return activeInventoryItems.filter((item) => bins.includes(item.bin_id))
  }, [bin, activeInventoryItems])

  const onMarkScan = useCallback(
    (item_id: string, isScanned = true) => {
      axios
        .post(`${getBaseURL()}/inventories/${activeInventoryMetadata?.id}/item/${item_id}/scan`, { isScanned })
        .then(() => refresh())
        .catch(() => {
          enqueueSnackbar('Server error while scanning', { variant: 'error' })
        })
    },
    [activeInventoryMetadata, refresh, enqueueSnackbar]
  )

  const onSaveCondition = useCallback(
    ({ assessed_qty, condition }: ItemConditionData) => {
      if (!itemUndergoingEdits) {
        return
      }
      axios
        .post(`${getBaseURL()}/inventories/${activeInventoryMetadata?.id}/item/${itemUndergoingEdits.id}/condition`, {
          assessed_qty,
          condition,
        })
        .then(() => {
          if (isPresentingMandatoryEditWindow) {
            onMarkScan(itemUndergoingEdits.id)
            setIsPresentingMandatoryEditWindow(false)
          } else {
            refresh()
          }
          setItemUndergoingEdits(undefined)
        })
        .catch(() => {
          enqueueSnackbar('Server error while saving', { variant: 'error' })
        })
    },
    [
      activeInventoryMetadata,
      refresh,
      isPresentingMandatoryEditWindow,
      itemUndergoingEdits,
      onMarkScan,
      enqueueSnackbar,
    ]
  )

  const performItemScanAction = useCallback(
    (id: string) => {
      setNotice(undefined)
      const item = items.find((i) => i.id.toLocaleUpperCase() === id.toLocaleUpperCase())

      // the item either does not exist, or the item belongs in a different tote
      if (!item) {
        axios
          .get(`${getBaseURL()}/item/${id}`)
          .then((data) => {
            const itemData = data.data as Item

            const correctBin = allBins.find((bin) => bin.id === itemData.bin_id)
            if (!correctBin) {
              enqueueSnackbar(`Error! Item has invalid bin!`, {
                variant: 'error',
              })
            } else {
              const parent = parentTote(correctBin.id, allBins)
              if (parent?.id === bin?.id) {
                if (authContext.tokenData && isAdmin(authContext.tokenData)) {
                  enqueueSnackbar(
                    `HEADS UP! That item was not a part of this event's inventory. Adding it now as an administrator.`,
                    { variant: 'success' }
                  )
                  axios
                    .put(`${getBaseURL()}/inventories/${activeInventoryMetadata?.id}/item/${itemData.id}`)
                    .then(() => onMarkScan(itemData.id))
                    .catch(() => {
                      enqueueSnackbar('Server error while adding item back to inventory', { variant: 'error' })
                      setNotice({
                        kind: 'error',
                        message: 'Server error while adding item back to inventory',
                      })
                    })
                } else {
                  enqueueSnackbar(
                    `HEADS UP! That item is not a part of this event's inventory. Please store it in this bin anyway.`,
                    { variant: 'warning' }
                  )
                  setNotice({
                    kind: 'warning',
                    message: `HEADS UP! That item is not a part of this event's inventory. Please store it in this bin anyway.`,
                    forceAck: true,
                    persistent: true,
                  })
                }
              } else {
                enqueueSnackbar(`Error! Wrong Bin! this item belongs in ${parent?.name}`, {
                  variant: 'error',
                })
                setNotice({
                  kind: 'error',
                  message: `Error! Wrong Bin! this item belongs in ${parent?.name}`,
                  forceAck: true,
                  persistent: true,
                })
              }
            }
          })
          .catch(() => {
            enqueueSnackbar(`Error! Invalid item ID: "${id}"`, {
              variant: 'error',
            })
            setNotice({ kind: 'error', message: `Error! Invalid item ID: "${id}"` })
          })
      } else {
        if (item.scanned_at) {
          enqueueSnackbar(`Already scanned.`, { variant: 'warning' })
          setNotice({ kind: 'warning', message: `Already scanned.` })
        }
        // the item needs to be marked off
        else if (item.qty > 1) {
          // items with specific quantities require that the user intervenes.
          setItemUndergoingEdits(item)
          setIsPresentingMandatoryEditWindow(true)
        } else {
          // mark as done
          onMarkScan(id)
          if (item.instructions) {
            setNotice({
              kind: 'success',
              forceAck: true,
              message: `Special Instructions: ${item.instructions}. Item belongs in ${
                allBins.find((bin) => bin.id === item.bin_id)?.name
              }.`,
            })
          } else {
            setNotice({
              kind: 'success',
              message: `Item belongs in ${allBins.find((bin) => bin.id === item.bin_id)?.name}`,
            })
          }
        }

        refresh()
      }
    },
    [items, refresh, allBins, enqueueSnackbar, bin?.id, authContext.tokenData, activeInventoryMetadata?.id, onMarkScan]
  )

  const onSearch = useCallback(() => {
    if (searchQuery) {
      performItemScanAction(searchQuery)
      setSearchQuery('')
    }
  }, [performItemScanAction, searchQuery])

  const deleteItem = useCallback(
    (item_id: string) => {
      axios
        .delete(`${getBaseURL()}/inventories/${activeInventoryMetadata?.id}/item/${item_id}`)
        .then(() => refresh())
        .catch(() => {
          enqueueSnackbar('Server error while deleting', { variant: 'error' })
        })
    },
    [activeInventoryMetadata?.id, enqueueSnackbar, refresh]
  )

  const itemRow = useCallback(
    (item: Item & InventoryItem) => (
      <tr key={item.id} style={item.scanned_at ? { backgroundColor: '#AFA' } : undefined}>
        <td>
          {item.scanned_at ? (
            <CheckBoxOutlined onClick={() => onMarkScan(item.id, false)} style={{ cursor: 'pointer' }} />
          ) : (
            <CheckBoxOutlineBlank style={{ cursor: 'pointer' }} onClick={() => performItemScanAction(item.id)} />
          )}
        </td>
        <td>{item.id}</td>
        <td>{item.name}</td>
        <td>{item.instructions}</td>
        <td>{item.qty}</td>
        <td
          style={{
            background: item.scanned_at && (item.assessed_qty ?? 1) < item.qty ? 'yellow' : '',
          }}
        >
          {item.scanned_at ? item.assessed_qty ?? 1 : '--'}
        </td>
        <td
          style={{
            background: item.scanned_at && item.condition ? 'yellow' : '',
          }}
        >
          {item.scanned_at ? (
            <>
              {item.condition ?? 'normal'}{' '}
              <Edit style={{ cursor: 'pointer' }} onClick={() => setItemUndergoingEdits(item)} />
            </>
          ) : (
            '--'
          )}
        </td>
        <td>{allBins.find((bin) => bin.id === item.bin_id)?.name ?? '--'}</td>
        <td>
          {authContext.tokenData && isAdmin(authContext.tokenData) && (
            <IconButton onClick={() => deleteItem(item.id)}>
              <Delete />
            </IconButton>
          )}
        </td>
      </tr>
    ),
    [allBins, authContext.tokenData, deleteItem, onMarkScan, performItemScanAction]
  )

  const [playWarning] = useSound(err_1)
  const [playError] = useSound(err_2, { volume: 1.2 })
  const [playError2] = useSound(err_3)
  const [playSuccess] = useSound(success_sound, { volume: 0.2 })
  const [playSuccessAck] = useSound(success_sound2)

  useEffect(() => {
    if (!notice) {
      searchFieldRef.current?.focus()
    } else {
      switch (notice.kind) {
        case 'error':
          if (notice.forceAck) {
            playError()
          } else {
            playError2()
          }
          break
        case 'warning':
          playWarning()
          break
        case 'success':
          if (notice.forceAck) {
            playSuccessAck()
          } else {
            playSuccess()
          }
          break
        default:
        // do nothing
      }
    }
  }, [notice, playWarning, playError, playError2, playSuccessAck, playSuccess])

  return (
    <div>
      <Link to="..">
        <ArrowBack /> Back to {activeInventoryMetadata?.name} overview
      </Link>
      <br />
      <br />
      <Typography variant="h4">Scanning items for {bin?.name}</Typography>
      <div
        style={{
          marginTop: 10,
          padding: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Typography>{bin?.description || 'No description provided'}</Typography>
      </div>
      <div
        style={{
          padding: 10,
          borderTop: '1px solid #DDD',
          borderBottom: '1px solid #DDD',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginBottom: 25,
        }}
      >
        <Typography fontWeight="bold">Packing Instructions</Typography>
        <Typography>{bin?.instructions || 'No packing instructions provided'}</Typography>
      </div>
      {notice && (
        <NoticeComponent
          notice={notice}
          onClear={() => {
            setNotice(undefined)
          }}
        />
      )}
      <br />
      <Paper
        elevation={3}
        style={{
          display: 'flex',
          padding: 30,
          gap: 20,
        }}
      >
        <TextField
          disabled={notice && notice.forceAck}
          inputRef={searchFieldRef}
          style={{ flexGrow: 1 }}
          placeholder={'Scan item barcode'}
          value={searchQuery}
          onKeyUp={(ev) => {
            if (ev.key === 'Enter') {
              onSearch()
            }
          }}
          onChange={(ev) => {
            setSearchQuery(ev.target.value)
          }}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <QrCode2 />
              </InputAdornment>
            ),
          }}
        />
        <Box style={{ marginTop: 8 }}>
          <IconButton
            onClick={() => {
              onSearch()
              searchFieldRef.current?.focus()
            }}
          >
            <PlaylistAddCheck />
          </IconButton>
        </Box>
      </Paper>
      <br />
      <BinProgressBar
        progress={{
          scanned: items.filter((i) => !!i.scanned_at).length,
          total: items.length,
          costScanned: items
            .filter((i) => !!i.scanned_at)
            .map((i) => parseFloat(String(i.cost ?? 0)))
            .reduce((a, b) => a + b, 0),
          totalCost: items.map((i) => parseFloat(String(i.cost ?? 0))).reduce((a, b) => a + b, 0),
        }}
      />
      <br />
      <table cellPadding={5}>
        <thead>
          <tr>
            <th></th>
            <th>id</th>
            <th>name</th>
            <th>instructions</th>
            <th>initial qty</th>
            <th>scanned qty</th>
            <th>condition</th>
            <th>where</th>
            <th></th>
          </tr>
        </thead>
        <tbody>{items.map((item) => itemRow(item))}</tbody>
      </table>
      <AssessItemDialog
        forItem={itemUndergoingEdits}
        warning={
          isPresentingMandatoryEditWindow
            ? `This item has ${itemUndergoingEdits?.qty ?? 1} parts. Please count and record the quantity`
            : undefined
        }
        onSave={onSaveCondition}
        onDismiss={() => {
          if (!isPresentingMandatoryEditWindow) {
            setItemUndergoingEdits(undefined)
          } else {
            enqueueSnackbar('This form is mandatory, please fill it in before scanning the next item.', {
              variant: 'error',
            })
          }
        }}
      />
    </div>
  )
}
